import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import AdminSubnav from "@/components/app/AdminSubnav";
import { Save, Mail, FileCheck2, Zap, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    accountant_email: "",
    accountant_name: "",
    cc_admins_on_approval: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const compressImages = async () => {
    if (!window.confirm("Compresser toutes les anciennes photos volumineuses ? Cette opération est sûre — elle ne supprime aucune photo, elle réduit juste leur taille pour accélérer la génération de PDF.")) return;
    setCompressing(true);
    try {
      const { data } = await api.post("/admin/compress-images", null, { timeout: 600000 });
      const candidates = data.candidates ?? data.processed ?? 0;
      if (data.processed > 0) {
        toast.success(`${data.processed} photo(s) compressée(s) — ${data.saved_mb || 0} Mo économisés`);
      } else if (candidates === 0) {
        toast.success("Toutes vos photos sont déjà optimisées ✓");
      } else if (data.failures > 0) {
        toast.warning(`${data.failures} photo(s) n'ont pas pu être compressées (format non reconnu). ${data.processed} traitées.`);
      } else {
        toast.info(`${candidates} photo(s) candidate(s) — déjà au minimum.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const networkErr = !e?.response;
      const msg = networkErr
        ? "La compression prend plus de temps que prévu. Réessayez dans 1 minute — elle continue en arrière-plan."
        : (detail || `Erreur serveur (${e?.response?.status || "?"})`);
      toast.error(msg);
    } finally {
      setCompressing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings({
        accountant_email: data?.accountant_email || "",
        accountant_name: data?.accountant_name || "",
        cc_admins_on_approval: data?.cc_admins_on_approval !== false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        accountant_email: settings.accountant_email.trim() || null,
        accountant_name: settings.accountant_name.trim() || null,
        cc_admins_on_approval: !!settings.cc_admins_on_approval,
      };
      const { data } = await api.put("/admin/settings", payload);
      setSettings({
        accountant_email: data?.accountant_email || "",
        accountant_name: data?.accountant_name || "",
        cc_admins_on_approval: data?.cc_admins_on_approval !== false,
      });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <AdminSubnav />
      <p className="overline mb-2">Administration</p>
      <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
        Paramètres
      </h1>
      <p className="text-gray-500 mb-8 max-w-lg">
        Configurez les destinataires automatiques des rapports validés.
      </p>

      {loading ? (
        <div className="ra-card p-8 text-center text-gray-400">Chargement...</div>
      ) : (
        <form onSubmit={save} className="space-y-6 max-w-2xl" data-testid="settings-form">
          <div className="ra-card p-5">
            <div className="flex items-start gap-3 mb-4">
              <FileCheck2 size={20} className="text-[#002FA7] shrink-0 mt-1" />
              <div>
                <h2 className="font-heading font-black text-lg tracking-tight">Destinataire comptable</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Lorsqu'un administrateur clique sur <strong>« Valider »</strong> un rapport, un courriel sera automatiquement envoyé à cette adresse avec le PDF des factures et le fichier Excel en pièces jointes.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="ra-label"><Mail size={12} className="inline mr-1" /> Courriel du comptable</label>
                <input
                  type="email"
                  className="ra-input"
                  placeholder="comptabilite@votre-entreprise.com"
                  value={settings.accountant_email}
                  onChange={(e) => setSettings((s) => ({ ...s, accountant_email: e.target.value }))}
                  data-testid="accountant-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">Laissez vide pour désactiver l'envoi automatique.</p>
              </div>
              <div>
                <label className="ra-label">Nom (optionnel)</label>
                <input
                  type="text"
                  className="ra-input"
                  placeholder="Service de comptabilité"
                  value={settings.accountant_name}
                  onChange={(e) => setSettings((s) => ({ ...s, accountant_name: e.target.value }))}
                  data-testid="accountant-name-input"
                />
                <p className="text-xs text-gray-500 mt-1">Utilisé dans la salutation du courriel (« Bonjour {"{nom}"} »).</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">Les notifications in-app et courriels aux utilisateurs restent actifs indépendamment.</p>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
              data-testid="save-settings-button"
            >
              <Save size={14} /> {saving ? "..." : "Enregistrer"}
            </button>
          </div>

          <div className="ra-card p-5 border-l-4 border-[#002FA7]">
            <div className="flex items-start gap-3 mb-3">
              <Zap size={20} className="text-[#002FA7] shrink-0 mt-1" />
              <div>
                <h2 className="font-heading font-black text-lg tracking-tight">Optimiser les images</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Compresse toutes les photos de factures de plus de 8 Mo (ex: HEIC iPhone non optimisés). Accélère la génération de PDF et le téléchargement sur mobile. <strong>Sans perte visible</strong> — les détails restent lisibles.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={compressImages}
              disabled={compressing}
              className="btn-secondary"
              data-testid="compress-images-button"
            >
              {compressing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              {compressing ? "Compression en cours..." : "Optimiser maintenant"}
            </button>
            <p className="text-xs text-gray-400 mt-2">⚡ Action recommandée si vos PDF mettent plus de 30 secondes à générer.</p>
          </div>
        </form>
      )}
    </Layout>
  );
}
